export const formatDate = (inputDate = '') => {
  if (!inputDate) return '';

  if (inputDate instanceof Date) {
    const day = String(inputDate.getDate()).padStart(2, '0');
    const month = String(inputDate.getMonth() + 1).padStart(2, '0');
    const year = inputDate.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const parts = inputDate.split('-');
  if (parts.length !== 3) {
    // Check for other delimiters if needed, but standard is -
    return inputDate; // Return original if not YYYY-MM-DD
  }

  const [year, month, day] = parts;
  // Ensure 2 digit padding if string lacks it
  const d = day.padStart(2, '0');
  const m = month.padStart(2, '0');
  return `${d}/${m}/${year}`;
};

export const formatDateTimeStampToDDMMMYYYY = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (isNaN(date)) {
    return null;
  }
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  const formattedDate = date.toLocaleDateString('en-US', options);
  const parts = formattedDate.split(' ');
  return `${parts[1]} ${parts[0]} ${parts[2]}`;
};

export const formatDateTimeStampToDDMMMYYYYhhmm = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) {
    return null;
  }
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };
  return date.toLocaleString('en-US', options);
};

export const getTimeElapsed = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) return null;

  const now = new Date();

  const isToday = date.toDateString() === now.toDateString();

  // ⬇️ If today → show elapsed time
  if (isToday) {
    const diff = now - date;
    const minute = 60 * 1000;
    const hour = 60 * minute;

    if (diff < minute) return 'just now';

    if (diff < hour) {
      const minutes = Math.floor(diff / minute);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }

    const hours = Math.floor(diff / hour);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }

  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function formatDateTime(timestamp) {
  if (!timestamp) return '';
  const date = new Date(Number(timestamp));
  if (isNaN(date)) {
    return null;
  }

  const options = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  return new Intl.DateTimeFormat('en-US', options).format(date);
}

/**
 * Converts a timestamp string from "YYYY-MM-DD HH:mm:ss.ssssss" format
 * to a proper ISO 8601 UTC string: "YYYY-MM-DDTHH:mm:ss.sssZ"
 *
 * @param {string} timestamp - Timestamp from server, e.g., "2025-09-25 18:56:52.261000"
 * @returns {string} ISO 8601 UTC string, e.g., "2025-09-25T18:56:52.261Z"
 */
export function toUTCFormat(timestamp) {
  if (!timestamp || typeof timestamp !== 'string') return '';

  // Replace space with T
  let iso = timestamp.replace(' ', 'T');

  // Truncate microseconds to milliseconds (3 digits)
  iso = iso.replace(/(\.\d{3})\d+/, '$1');

  // Append Z if not already present to indicate UTC
  if (!iso.endsWith('Z')) {
    iso += 'Z';
  }

  return iso;
}

/**
 * Formats a UTC timestamp for display in a local timezone.
 *
 * Rules:
 * 1. Today -> returns time in "h:mm AM/PM" format
 * 2. Yesterday -> returns "Yesterday"
 * 3. Older dates -> returns "DD/MM/YYYY"
 *
 * @param {string|Date} timestamp - ISO 8601 UTC timestamp or Date object
 * @param {Object} options
 * @param {string} [options.timeZone='Asia/Kolkata'] - IANA timezone string
 * @returns {string} Formatted timestamp
 */
export function formatRecencyDate(timestamp, options = {}) {
  const { timeZone = 'Asia/Kolkata' } = options;
  if (!timestamp) return '';

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return '';

  const getDateOnly = (d) => {
    const dtf = new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone,
    });
    const [{ value: day }, , { value: month }, , { value: year }] =
      dtf.formatToParts(d);
    return `${year}-${month}-${day}`;
  };

  const todayStr = getDateOnly(new Date());
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = getDateOnly(yesterday);
  const dateStr = getDateOnly(date);

  if (dateStr === todayStr) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone,
    });
  }

  if (dateStr === yesterdayStr) {
    return 'Yesterday';
  }

  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}
